import { KeyboardEventHandler, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import EmployeeLookupSection from "../components/intake/EmployeeLookupSection";
import PageSectionCard from "../components/PageSectionCard";
import { useAppSession } from "../context/AppSessionContext";
import { readErrorMessage } from "../lib/http";
import { Employee } from "../types/employees";
import { PaginatedResponse } from "../types/pagination";

const searchLimit = 8;

function CreateEpisodePage() {
  const { t } = useTranslation();
  const { authorizedFetch } = useAppSession();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Employee[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [highlightedResultIndex, setHighlightedResultIndex] = useState(0);
  const [isLookupOpen, setIsLookupOpen] = useState(false);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 250);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  useEffect(() => {
    const trimmedSearch = debouncedSearchTerm.trim();

    if (!trimmedSearch) {
      setSearchResults([]);
      setSearchError(null);
      setIsSearching(false);
      setHighlightedResultIndex(0);
      return;
    }

    let cancelled = false;

    const loadEmployees = async () => {
      setIsSearching(true);
      setSearchError(null);

      try {
        const query = new URLSearchParams({
          page: "1",
          limit: String(searchLimit),
          search: trimmedSearch,
        });

        const response = await authorizedFetch(`/employees?${query.toString()}`);

        if (!response.ok) {
          throw new Error(
            await readErrorMessage(
              response,
              t("Failed to search employees ({{status}}).", {
                status: response.status,
              }),
            ),
          );
        }

        const payload = (await response.json()) as PaginatedResponse<Employee>;

        if (cancelled) {
          return;
        }

        setSearchResults(payload.data ?? []);
        setHighlightedResultIndex(0);
      } catch (error) {
        if (cancelled) {
          return;
        }

        const message =
          error instanceof Error ? error.message : t("Failed to search employees.");
        setSearchError(message);
        setSearchResults([]);
      } finally {
        if (!cancelled) {
          setIsSearching(false);
        }
      }
    };

    void loadEmployees();

    return () => {
      cancelled = true;
    };
  }, [authorizedFetch, debouncedSearchTerm, t]);

  const handleSelectEmployee = (employee: Employee) => {
    navigate(`/employees/${employee.id}`);
  };

  const onSearchInputKeyDown: KeyboardEventHandler<HTMLInputElement> = (
    event,
  ) => {
    const resultsCount = searchResults.length;

    if (event.key === "ArrowDown" && resultsCount > 0) {
      event.preventDefault();
      setIsLookupOpen(true);
      setHighlightedResultIndex((current) => (current + 1) % resultsCount);
      return;
    }

    if (event.key === "ArrowUp" && resultsCount > 0) {
      event.preventDefault();
      setIsLookupOpen(true);
      setHighlightedResultIndex(
        (current) => (current - 1 + resultsCount) % resultsCount,
      );
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();

      if (resultsCount > 0) {
        const employee =
          searchResults[highlightedResultIndex] ?? searchResults[0] ?? null;

        if (employee) {
          handleSelectEmployee(employee);
        }
      }

      return;
    }

    if (event.key === "Escape") {
      setIsLookupOpen(false);
    }
  };

  return (
    <PageSectionCard
      titleId="create-episode-title"
      eyebrow={t("Incapacity")}
      title={t("Find an employee")}
      lead={t("Search for an employee to open their page and start an episode.")}
    >
      <div className="intakeFormLayout">
        <EmployeeLookupSection
          searchTerm={searchTerm}
          onSearchTermChange={(value) => {
            setSearchTerm(value);
            setIsLookupOpen(true);
          }}
          onSearchInputKeyDown={onSearchInputKeyDown}
          onSearchFocus={() => setIsLookupOpen(true)}
          onSearchBlur={() => {
            window.setTimeout(() => {
              setIsLookupOpen(false);
            }, 100);
          }}
          onSelectEmployee={handleSelectEmployee}
          searchResults={searchResults}
          highlightedResultIndex={highlightedResultIndex}
          onHighlightResult={setHighlightedResultIndex}
          isSearching={isSearching}
          searchError={searchError}
          isLookupOpen={isLookupOpen}
        />
      </div>
    </PageSectionCard>
  );
}

export default CreateEpisodePage;
